import React, { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import { getActiveEvent, getBillettyper } from '../lib/cms';

export default function PriorityCykler() {
  const [eventData, setEventData] = useState(null);
  const [ticketData, setTicketData] = useState(null);

  useEffect(() => {
    getActiveEvent().then(setEventData);
    getBillettyper().then(tickets => {
      const p = tickets.find(t => t.titel.toLowerCase().includes('priority'));
      if (p) setTicketData(p);
    });
  }, []);

  const billetlink = eventData?.billetlink || "https://billet.eventbilletten.dk/seller/foreningen-rbike-zd1k";

  const fordele = [
    { icon: "fa-trophy", titel: "Plads i de 3 forreste rækker", tekst: "Optimal stemning, energi og udsyn til scenen og instruktørerne." },
    { icon: "fa-tv", titel: "Logo på storskærme", tekst: "Eksponering under pauser i både cykelsalen og spisesalen hele dagen." },
    { icon: "fa-video", titel: "20–30 sekunders profilvideo", tekst: "Mulighed for at få vist jeres virksomhedsvideo på storskærme." },
    { icon: "fa-thumbs-up", titel: "Logo på ØrBikes Facebook", tekst: "Profilering på ØrBikes officielle SoMe-tidslinje for tusindvis af følgere." },
    { icon: "fa-utensils", titel: "Reserveret spisebord", tekst: "Mulighed for at reservere et bord med 10 pladser i spisesalen til jeres team." },
    { icon: "fa-camera-retro", titel: "Logo på sponsorvæg", tekst: "Synlighed på den officielle selfie-stand, hvor deltagere tager billeder." },
    { icon: "fa-bullhorn", titel: "Opstilling af roll-up / stand", tekst: "Mulighed for roll-ups, vareprøver eller rekrutteringsstand under eventet." },
    { icon: "fa-heart", titel: "Støt en god sag", tekst: "Vis jeres CSR-engagement og bidrag direkte til børn med kritisk sygdom." }
  ];

  return (
    <div className="bg-[#090a0f] text-slate-100 min-h-screen pb-20">
      <Helmet>
        <title>Priority Cykler - Foreningen ØrBike</title>
        <meta
          name="description"
          content="Bliv firmasponsor til ØrBike og få en Priority Cykel i de tre forreste rækker med op til 10 ryttere og stærk logoeksponering."
        />
        <meta property="og:title" content="Priority Cykler - Foreningen ØrBike" />
        <meta property="og:description" content="Tag dit team med til Danmarks største bike fest og profilér din virksomhed for en god sag." />
      </Helmet>

      {/* Hero Header */}
      <section className="relative pt-36 sm:pt-40 pb-16 sm:pb-20 bg-gradient-to-b from-[#181126] to-[#090a0f] border-b border-pink-500/20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto text-center">
          <span className="inline-block px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wider bg-pink-500/20 text-pink-400 border border-pink-500/30 mb-4">
            For virksomheder, firmahold & sponsorer
          </span>
          <h1 className="text-4xl sm:text-6xl font-black font-display text-white uppercase tracking-tight">
            Priority Cykler
          </h1>
          <p className="text-pink-300 font-display text-lg sm:text-2xl font-bold uppercase mt-2">
            Bliv firmasponsor og spin for en god sag
          </p>
        </div>
      </section>

      <section className="py-16 max-w-[1520px] mx-auto px-4 sm:px-6 lg:px-8 space-y-16">
        {/* Intro */}
        <div className="grid grid-cols-1 md:grid-cols-12 gap-8 items-center">
          <div className="md:col-span-7 space-y-4">
            <h2 className="text-2xl sm:text-4xl font-black text-white font-display uppercase tracking-tight">
              Tag dit team med til en dag fyldt med energi og fællesskab
            </h2>
            <p className="text-slate-300 text-base leading-relaxed">
              Med en <strong>priority cykel til ØrBike</strong> får du en helt særlig mulighed for at samle kollegaer, samarbejdspartnere eller kunder til en dag, der kombinerer <strong>energi, fællesskab og god stemning</strong>.
            </p>
            <p className="text-slate-300 text-base leading-relaxed">
              I bliver en del af en <strong>10-timers indoor bike fest</strong>, hvor smil, sved og sammenhold går hånd i hånd – med dygtige instruktører, motiverende musik og en fantastisk beliggenhed <strong>midt i Danmark</strong>.
            </p>
            <p className="text-slate-400 text-sm leading-relaxed">
              Det er den perfekte måde at styrke relationer, skabe nye oplevelser sammen og samtidig støtte et godt formål.
            </p>
          </div>

          <div className="md:col-span-5">
            <div className="rounded-2xl border border-pink-500/30 overflow-hidden shadow-[0_0_30px_rgba(255,42,141,0.2)]">
              <img
                src="https://assets.auxodev.dk/assets/c6ea7980-b639-49a1-80cc-f044c1665109/dreamfour-9804.jpg"
                alt="Priority Cykel Stemning"
                className="w-full h-80 object-cover"
              />
            </div>
          </div>
        </div>

        {/* Priskort */}
        <div className="bg-gradient-to-b from-[#181a29] to-[#0f111d] border-2 border-pink-500 rounded-3xl p-8 sm:p-12 shadow-[0_0_35px_rgba(255,42,141,0.25)] flex flex-col md:flex-row items-center justify-between gap-8">
          <div className="space-y-2 text-center md:text-left">
            <span className="text-xs uppercase font-mono text-pink-400 font-bold">Effektiv profilering & medarbejderdag</span>
            <h3 className="text-3xl sm:text-4xl font-black font-display text-white uppercase">
              Priority Cykel 2026
            </h3>
            <div className="text-5xl font-black font-display text-white mt-2">
              {ticketData?.pris || "5.550 kr."}
            </div>
            <div className="text-xs text-slate-400">{ticketData?.pris_detalje || "inkl. moms pr. cykel - ekskl. ordregebyr"}</div>
            <p className="text-xs sm:text-sm text-slate-300 pt-2">
              Mulighed for 10 deltagere pr. cykel (f.eks. 1 ny rytter pr. time) • Valgfri placering i række 1-3.
            </p>
          </div>

          <div className="shrink-0 flex flex-col gap-3 w-full md:w-auto">
            <a
              href={billetlink}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center justify-center gap-2 px-8 py-4 rounded-full bg-gradient-to-r from-pink-500 to-purple-600 text-white font-bold text-sm uppercase tracking-wider shadow-lg hover:scale-105 transition-transform"
            >
              <i className="fa-solid fa-ticket" aria-hidden="true"></i>
              <span>Køb Priority Cykel nu</span>
            </a>
            <Link
              to="/kontakt"
              className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-full bg-slate-900 border border-slate-700 text-slate-300 hover:text-white text-xs uppercase font-semibold transition-colors"
            >
              <span>Spørgsmål om firmasponsorat? Kontakt os</span>
            </Link>
          </div>
        </div>

        {/* Hvad I får som firmasponsor */}
        <div className="space-y-8">
          <div className="text-center max-w-2xl mx-auto">
            <span className="text-xs uppercase tracking-widest font-bold text-purple-400 font-mono">
              Omfattende pakke
            </span>
            <h3 className="text-3xl sm:text-4xl font-black text-white font-display uppercase tracking-tight mt-1">
              Som firmasponsor får I:
            </h3>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {fordele.map((f, i) => (
              <div
                key={i}
                className="p-6 rounded-2xl bg-[#12141f] border border-slate-800 hover:border-pink-500/40 transition-colors flex flex-col justify-between"
              >
                <div>
                  <div className="w-10 h-10 rounded-xl bg-pink-500/20 text-pink-400 flex items-center justify-center text-lg mb-3">
                    <i className={`fa-solid ${f.icon}`} aria-hidden="true"></i>
                  </div>
                  <h4 className="font-bold text-white font-display text-lg uppercase mb-1">{f.titel}</h4>
                  <p className="text-xs text-slate-400 leading-relaxed">{f.tekst}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
